import React from 'react';
import classNames from 'classnames';
import { gql } from '@apollo/client';
import { Constrain, Stack, useBreakpoint } from '@moda/om';
import { ProductDetailPageFragment } from '../../generated/types';
import { RECOMMENDATIONS_VARIANT_FRAGMENT } from '../../hooks/useRecommendations';
import { EnableLazyLoad } from '../../components/LazyLoad';
import {
  PRODUCT_BREADCRUMBS_FRAGMENT,
  ProductBreadcrumbs
} from '../../components/ProductBreadcrumbs';
import { PRODUCT_DETAILS_FRAGMENT, ProductDetails } from '../../components/ProductDetails';
import {
  PRODUCT_MEDIA_GALLERY_FRAGMENT,
  ProductMediaGallery
} from '../../components/ProductMediaGallery';
import { ProductRecommendations } from '../../components/ProductRecommendations';
import { PRODUCT_SHOP_LINKS_FRAGMENT, ProductShopLinks } from '../../components/ProductShopLinks';
import {
  SCHEMA_ORG_PRODUCT_BREADCRUMBS_FRAGMENT,
  SCHEMA_ORG_PRODUCT_DETAIL_PAGE_FRAGMENT,
  SchemaOrgProductBreadcrumbs,
  SchemaOrgProductDetailPage
} from '../../components/SchemaOrg';
import { SoldOutProductRecommendations } from '../../components/SoldOutProductRecommendations';
import { PRODUCT_DETAIL_PAGE_META_FRAGMENT, ProductDetailPageMeta } from './ProductDetailPageMeta';

import './ProductDetailPage.scss';

export const PRODUCT_DETAIL_PAGE_FRAGMENT = gql`
  fragment ProductDetailPageFragment on Variant {
    id
    canSell: can_sell
    masterVariant: master_variants_data {
      id
    }
    otherVariants: other_variants_data {
      id
    }
    ...ProductDetailPageMetaFragment
    ...ProductBreadcrumbsFragment
    ...ProductDetailsFragment
    ...ProductMediaGalleryFragment
    ...ProductShopLinksFragment
    ...SchemaOrgProductDetailPageFragment
    ...SchemaOrgProductBreadcrumbsFragment
    ...RecommendationsVariantFragment
  }
  ${PRODUCT_DETAIL_PAGE_META_FRAGMENT}
  ${PRODUCT_BREADCRUMBS_FRAGMENT}
  ${PRODUCT_DETAILS_FRAGMENT}
  ${PRODUCT_MEDIA_GALLERY_FRAGMENT}
  ${PRODUCT_SHOP_LINKS_FRAGMENT}
  ${SCHEMA_ORG_PRODUCT_DETAIL_PAGE_FRAGMENT}
  ${SCHEMA_ORG_PRODUCT_BREADCRUMBS_FRAGMENT}
  ${RECOMMENDATIONS_VARIANT_FRAGMENT}
`;

interface Props {
  variant: ProductDetailPageFragment;
}

export const ProductDetailPage: React.FC<Props> = ({ variant }) => {
  const { matches: isMobile } = useBreakpoint({ lt: 'md' });

  return (
    <>
      <ProductDetailPageMeta variant={variant} />
      <SchemaOrgProductDetailPage variant={variant} />
      <SchemaOrgProductBreadcrumbs variant={variant} />

      {!variant.canSell && <SoldOutProductRecommendations variantId={variant.id} />}

      <Constrain>
        <Stack space={6} className="ProductDetailPage" data-variant-id={variant.id}>
          <ProductBreadcrumbs variant={variant} />

          <div className="ProductDetailPage__fold">
            <div
              className="ProductDetailPage__media"
              data-variant-pk={variant.id}
              data-product-id={variant.productId}
            >
              <ProductMediaGallery variant={variant} />
            </div>

            <div
              className={classNames('ProductDetailPage__metadata', {
                'ProductDetailPage__metadata--wide': !isMobile
              })}
            >
              <ProductDetails variant={variant} />
            </div>
          </div>

          <ProductShopLinks variant={variant} />

          <EnableLazyLoad>
            <ProductRecommendations variant={variant} />
          </EnableLazyLoad>
        </Stack>
      </Constrain>
    </>
  );
};
